import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { LuCheck } from 'react-icons/lu'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { getStoredUserProfile } from '../utils/authStorage'
import { buildApiUrl } from '../lib/api'
import Modal from '../components/common/Modal'
import { auth } from '../lib/firebase'
import { updateUserPlanCache } from '../hooks/useUserPlan'
import { fetchUserPlanDetails } from '../utils/planApi'
import { resolveUserIdentity } from '../utils/userIdentity'
import useLocale from '../hooks/useLocale'
import '../styles/Layout.css'
import '../styles/UpgradePage.css'

const PRO_PRICE_YEN = (() => {
  const raw = process.env.REACT_APP_PRO_PLAN_AMOUNT_YEN || '500'
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 500
})()

const FREE_PLAN_SCORE_LIMIT = (() => {
  const raw = process.env.REACT_APP_FREE_PLAN_SCORE_LIMIT || '3'
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 3
})()

const UPGRADE_TEXT = {
  ja: {
    kicker: 'PLANS',
    title: 'プランを選ぶ',
    subtitle: '保存スコア上限の拡張と広告の非表示を利用できます。',
    freeTag: 'FREE',
    proTag: 'PRO',
    freeTitle: 'Chord Analyzer FREE',
    proTitle: 'Chord Analyzer PRO',
    freeDescription: `無料プランでは保存スコアは${FREE_PLAN_SCORE_LIMIT}件までです。`,
    proDescription: '保存スコア無制限、広告非表示の買い切りプランです。',
    freeFeatures: [
      `保存スコア ${FREE_PLAN_SCORE_LIMIT}件まで`,
      '広告表示あり',
    ],
    proFeatures: [
      '保存スコア 無制限',
      '広告非表示',
    ],
    nowUsing: '現在このプランを利用中です。',
    checkoutButton: 'PROプランを利用する',
    checkoutLoading: '決済ページへ移動中...',
    checkoutError: '決済ページの作成に失敗しました。しばらくしてから再度お試しください。',
    checkoutNotConfigured: '決済機能の設定が未完了です（Stripe Secret Key未設定）。管理者設定後に再度お試しください。',
    checkoutRouteMissing: '決済APIが未デプロイです。バックエンドを最新化してください。',
    loadPlanError: 'プラン状態の取得に失敗しました。',
    checkoutSuccess: '決済が完了しました。PROプランを有効化しています。',
    checkoutCancel: '決済はキャンセルされました。',
    alreadyPro: 'すでにPROプランをご利用中です。',
    freePlanPassive: '無料プラン',
    refresh: '再読み込み',
    freeSwitch: 'FREEに切り替える', restorePro: 'PROに戻す', switching: '切り替え中...',
    freeConfirmTitle: 'FREEプランに切り替えますか？',
    freeConfirmCopy: `保存済みのスコアは残ります。新規作成は${FREE_PLAN_SCORE_LIMIT}件までとなり、広告が表示されます。購入済みのPRO利用権は保持され、いつでもPROに戻せます。`,
    cancel: 'キャンセル', changedFree: 'FREEプランに切り替えました。', changedPro: 'PROプランに戻しました。',
    changeError: 'プランを変更できませんでした。再度お試しください。', loginAgain: 'もう一度ログインしてからお試しください。',
    once: '買い切り・1回のみ', loadingPlan: 'プランを確認中...',
  },
  en: {
    kicker: 'PLANS',
    title: 'Choose your plan',
    subtitle: 'Unlock more saved scores and remove ads.',
    freeTag: 'FREE',
    proTag: 'PRO',
    freeTitle: 'Chord Analyzer FREE',
    proTitle: 'Chord Analyzer PRO',
    freeDescription: `The FREE plan allows up to ${FREE_PLAN_SCORE_LIMIT} saved scores.`,
    proDescription: 'A one-time upgrade with unlimited saved scores and no ads.',
    freeFeatures: [
      `Up to ${FREE_PLAN_SCORE_LIMIT} saved scores`,
      'Ads shown',
    ],
    proFeatures: [
      'Unlimited saved scores',
      'No ads',
    ],
    nowUsing: 'You are currently using this plan.',
    checkoutButton: 'Upgrade to PRO',
    checkoutLoading: 'Redirecting to Checkout...',
    checkoutError: 'Failed to create Checkout session. Please try again later.',
    checkoutNotConfigured: 'Checkout is not configured yet (Stripe secret key is missing).',
    checkoutRouteMissing: 'Checkout API is not deployed yet. Please deploy backend updates.',
    loadPlanError: 'Failed to load current plan.',
    checkoutSuccess: 'Payment completed. Activating your PRO plan.',
    checkoutCancel: 'Checkout was canceled.',
    alreadyPro: 'You are already on PRO.',
    freePlanPassive: 'Free plan',
    refresh: 'Refresh',
    freeSwitch: 'Switch to FREE', restorePro: 'Restore PRO', switching: 'Switching...',
    freeConfirmTitle: 'Switch to FREE?',
    freeConfirmCopy: `Your saved scores will remain. New scores are limited to ${FREE_PLAN_SCORE_LIMIT} and ads will be shown. Your purchased PRO access is kept, so you can restore PRO at any time.`,
    cancel: 'Cancel', changedFree: 'Switched to FREE.', changedPro: 'Restored PRO.',
    changeError: 'Could not change your plan. Please try again.', loginAgain: 'Please sign in again and retry.',
    once: 'One-time purchase', loadingPlan: 'Checking your plan...',
  },
}

function formatYen(locale, amount) {
  const normalizedLocale = locale === 'ja' ? 'ja-JP' : 'en-US'
  try {
    return new Intl.NumberFormat(normalizedLocale, {
      style: 'currency',
      currency: 'JPY',
      maximumFractionDigits: 0,
    }).format(amount)
  } catch (_error) {
    return `¥${amount}`
  }
}

function getQueryParam(paramName) {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get(paramName)
}

export default function UpgradePage({ user: userProp }) {
  const locale = useLocale()
  const text = UPGRADE_TEXT[locale] || UPGRADE_TEXT.en
  const fallbackUser = useMemo(() => getStoredUserProfile(), [])
  const user = userProp || fallbackUser
  const identity = resolveUserIdentity(user)
  const { email, userId } = identity
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [plan, setPlan] = useState('FREE')
  const [entitlementPlan, setEntitlementPlan] = useState('FREE')
  const [isChangingPlan, setIsChangingPlan] = useState(false)
  const [isFreeConfirmOpen, setIsFreeConfirmOpen] = useState(false)
  const [isLoadingPlan, setIsLoadingPlan] = useState(true)
  const [isStartingCheckout, setIsStartingCheckout] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [noticeMessage, setNoticeMessage] = useState('')

  const fetchPlan = useCallback(async () => {
    if (!userId) {
      setPlan('FREE')
      setIsLoadingPlan(false)
      return
    }

    try {
      setIsLoadingPlan(true)
      setErrorMessage('')
      const details = await fetchUserPlanDetails({ email, userId })
      setPlan(details.plan)
      setEntitlementPlan(details.entitlementPlan)
    } catch (error) {
      console.error('Failed to fetch plan', error)
      setErrorMessage(text.loadPlanError)
      setPlan('FREE')
    } finally {
      setIsLoadingPlan(false)
    }
  }, [email, text.loadPlanError, userId])

  useEffect(() => {
    fetchPlan()
  }, [fetchPlan])

  useEffect(() => {
    const checkoutResult = getQueryParam('checkout')
    if (checkoutResult === 'success') {
      setNoticeMessage(text.checkoutSuccess)
      fetchPlan()
    } else if (checkoutResult === 'cancel') {
      setNoticeMessage(text.checkoutCancel)
    }
  }, [fetchPlan, text.checkoutCancel, text.checkoutSuccess])

  const handleCheckout = async () => {
    if (!userId || isStartingCheckout) return

    setIsStartingCheckout(true)
    setErrorMessage('')
    setNoticeMessage('')

    try {
      const response = await fetch(buildApiUrl('/create-checkout-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          email: user?.email || '',
          locale,
        }),
      })
      const data = await response.json().catch(() => ({}))
      const backendMessage = String(data?.message || data?.error || '')
      if (!response.ok) {
        if (response.status === 403 && /missing authentication token/i.test(backendMessage)) {
          setErrorMessage(text.checkoutRouteMissing)
          return
        }
        if (/stripe secret key is not configured/i.test(backendMessage)) {
          setErrorMessage(text.checkoutNotConfigured)
          return
        }
        setErrorMessage(
          backendMessage
            ? `${text.checkoutError} (${backendMessage})`
            : text.checkoutError
        )
        return
      }

      if (data.alreadyPro) {
        setPlan('PRO')
        setNoticeMessage(text.alreadyPro)
        return
      }

      if (!data.checkoutUrl) {
        throw new Error('Missing checkoutUrl')
      }

      window.location.href = data.checkoutUrl
    } catch (error) {
      console.error('Failed to start checkout', error)
      setErrorMessage(text.checkoutError)
    } finally {
      setIsStartingCheckout(false)
    }
  }

  const handlePlanChange = async (preferredPlan) => {
    if (isChangingPlan || isLoadingPlan || isStartingCheckout) return
    setIsChangingPlan(true)
    setErrorMessage('')
    setNoticeMessage('')
    try {
      await auth.authStateReady()
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error('LOGIN_REQUIRED')
      const response = await fetch(buildApiUrl('/change-plan'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ preferredPlan }),
      })
      const data = await response.json().catch(() => ({}))
      if (response.status === 401) throw new Error('LOGIN_REQUIRED')
      if (!response.ok) throw new Error('CHANGE_FAILED')
      setPlan(data.plan)
      setEntitlementPlan(data.entitlementPlan)
      updateUserPlanCache(user, data.plan)
      setIsFreeConfirmOpen(false)
      setNoticeMessage(data.plan === 'FREE' ? text.changedFree : text.changedPro)
    } catch (error) {
      setErrorMessage(error.message === 'LOGIN_REQUIRED' ? text.loginAgain : text.changeError)
    } finally { setIsChangingPlan(false) }
  }

  const isPro = plan === 'PRO'
  const priceLabel = formatYen(locale, PRO_PRICE_YEN)

  return (
    <div className="page-shell">
      <Header user={user} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <main className="page-main upgrade-main">
        <section className="upgrade-shell">
          <div className="upgrade-hero">
            <p className="upgrade-kicker">{text.kicker}</p>
            <h1 className="upgrade-title">{text.title}</h1>
            <p className="upgrade-subtitle">{text.subtitle}</p>
          </div>

          {noticeMessage && <div className="upgrade-notice">{noticeMessage}</div>}
          {errorMessage && <div className="upgrade-error">{errorMessage}</div>}

          <div className="upgrade-grid">
            <article className="upgrade-card upgrade-card--free">
              <span className="upgrade-card-badge">{text.freeTag}</span>
              <h2 className="upgrade-card-title">{text.freeTitle}</h2>
              <p className="upgrade-card-description">{text.freeDescription}</p>
              <p className="upgrade-price">¥0</p>
              <p className="upgrade-price-note">{text.freePlanPassive}</p>
              <ul className="upgrade-feature-list">
                {text.freeFeatures.map((item) => (
                  <li className="upgrade-feature-item" key={item}>
                    <LuCheck className="upgrade-feature-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {isLoadingPlan ? <div className="upgrade-plan-state" role="status">{text.loadingPlan}</div>
                : isPro ? <button type="button" className="upgrade-checkout-button upgrade-checkout-button--free" onClick={() => setIsFreeConfirmOpen(true)} disabled={isChangingPlan}>{text.freeSwitch}</button>
                : <div className="upgrade-plan-state">{text.nowUsing}</div>}

            </article>

            <article className="upgrade-card upgrade-card--pro">
              <span className="upgrade-card-badge upgrade-card-badge--pro">{text.proTag}</span>
              <h2 className="upgrade-card-title">{text.proTitle}</h2>
              <p className="upgrade-card-description">{text.proDescription}</p>
              <p className="upgrade-price">{priceLabel}</p>
              <p className="upgrade-price-note">{text.once}</p>
              <ul className="upgrade-feature-list">
                {text.proFeatures.map((item) => (
                  <li className="upgrade-feature-item" key={item}>
                    <LuCheck className="upgrade-feature-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="upgrade-checkout-button"
                disabled={isPro || isStartingCheckout || isLoadingPlan || isChangingPlan}
                onClick={entitlementPlan === 'PRO' ? () => handlePlanChange('PRO') : handleCheckout}
              >
                {isLoadingPlan ? text.loadingPlan : isPro ? text.nowUsing : isChangingPlan ? text.switching : isStartingCheckout ? text.checkoutLoading : entitlementPlan === 'PRO' ? text.restorePro : text.checkoutButton}
              </button>
            </article>
          </div>
        </section>
      </main>
      <Modal isOpen={isFreeConfirmOpen} onClose={() => { if (!isChangingPlan) setIsFreeConfirmOpen(false) }} contentClassName="modal-content--modern" contentProps={{ role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'free-confirm-title' }}>
        <h2 id="free-confirm-title">{text.freeConfirmTitle}</h2>
        <p className="upgrade-confirm-copy">{text.freeConfirmCopy}</p>
        <div className="modal-buttons">
          <button type="button" className="cancel" disabled={isChangingPlan} onClick={() => setIsFreeConfirmOpen(false)}>{text.cancel}</button>
          <button type="button" className="create" disabled={isChangingPlan} onClick={() => handlePlanChange('FREE')}>{isChangingPlan ? text.switching : text.freeSwitch}</button>
        </div>
      </Modal>
      <Footer />
    </div>
  )
}
